import { Link } from "react-router-dom";
import DantesShell from "@/components/DantesShell";
import { getDantesContent } from "@/content/dantes";
import { useLang } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";

const stats = [
  "Memo",
  "Debt Stack",
  "Risk Matrix",
  "Owner Profile",
  "Client Letter",
];

export default function DantesPage() {
  const { lang } = useLang();
  const copy = getDantesContent(lang);
  usePageMeta(
    `Dantes by Leongalt`,
    copy.hero.body,
  );

  return (
    <DantesShell section="overview">
      <section className="border-b border-[var(--rule)]">
        <div className="mx-auto grid max-w-[1240px] gap-16 px-6 pb-20 pt-16 md:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)] md:px-10 md:pb-28 md:pt-24">
          <div>
            <p className="mb-5 font-sans text-[11px] uppercase tracking-[0.22em] text-[var(--gold)]">
              {copy.hero.eyebrow}
            </p>
            <h1 className="max-w-[12ch] font-serif text-[clamp(3rem,9vw,6.8rem)] leading-[0.9] text-[var(--text-primary)]">
              {copy.hero.title}
            </h1>
            <p className="mt-8 max-w-[54ch] font-sans text-[16px] leading-8 text-[var(--text-secondary)]">
              {copy.hero.body}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={process.env.REACT_APP_DANTES_APP_URL || "/login"}
                className="border border-[rgba(158,138,90,0.5)] bg-[rgba(158,138,90,0.1)] px-5 py-3 font-sans text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] transition-colors hover:border-[var(--gold)] hover:text-[var(--text-primary)]"
              >
                {copy.hero.primary}
              </a>
              <Link
                to="/dantes/pricing"
                className="border border-[var(--rule-strong)] px-5 py-3 font-sans text-[11px] uppercase tracking-[0.18em] text-[var(--text-primary)] transition-colors hover:border-[var(--text-primary)]"
              >
                {copy.hero.secondary}
              </Link>
            </div>
            <p className="mt-8 max-w-[52ch] font-sans text-[12px] leading-7 text-[var(--text-tertiary)]">
              {copy.hero.proof}
            </p>
          </div>

          <div className="self-end border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] p-6">
            <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-[var(--gold)]">
              Live decision surface
            </p>
            <div className="mt-6 space-y-5">
              {stats.map((item, index) => (
                <div key={item} className="border-b border-[var(--rule)] pb-4 last:border-b-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <span className="font-sans text-[11px] uppercase tracking-[0.14em] text-[var(--text-tertiary)]">
                      0{index + 1}
                    </span>
                    <span className="font-sans text-[13px] text-[var(--text-primary)]">{item}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--rule)]">
        <div className="mx-auto grid max-w-[1240px] gap-10 px-6 py-16 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:px-10">
          <div>
            <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">
              Workflow
            </p>
            <h2 className="mt-4 max-w-[14ch] font-serif text-[clamp(2.2rem,5vw,4rem)] leading-[0.95]">
              {copy.workflow.title}
            </h2>
          </div>
          <div>
            <p className="max-w-[54ch] font-sans text-[15px] leading-8 text-[var(--text-secondary)]">
              {copy.workflow.body}
            </p>
            <div className="mt-8 space-y-5">
              {copy.workflow.steps.map((step, index) => (
                <div key={step} className="grid gap-3 border-t border-[var(--rule)] pt-4 md:grid-cols-[56px_minmax(0,1fr)]">
                  <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-[var(--text-tertiary)]">
                    0{index + 1}
                  </span>
                  <p className="font-sans text-[14px] leading-7 text-[var(--text-primary)]">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--rule)]">
        <div className="mx-auto grid max-w-[1240px] gap-12 px-6 py-16 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:px-10">
          <div>
            <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">
              {copy.artifact.kicker}
            </p>
            <h2 className="mt-4 max-w-[14ch] font-serif text-[clamp(2.2rem,5vw,4rem)] leading-[0.95]">
              {copy.artifact.title}
            </h2>
            <p className="mt-6 max-w-[50ch] font-sans text-[15px] leading-8 text-[var(--text-secondary)]">
              {copy.artifact.body}
            </p>
          </div>

          <div className="space-y-6">
            {copy.artifact.stats.map(([label, text]) => (
              <div key={label} className="border-t border-[var(--rule)] pt-5">
                <p className="font-sans text-[10px] uppercase tracking-[0.16em] text-[var(--text-tertiary)]">
                  {label}
                </p>
                <p className="mt-2 font-sans text-[14px] leading-7 text-[var(--text-primary)]">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--rule)]">
        <div className="mx-auto max-w-[1240px] px-6 py-16 md:px-10">
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">
                Personas
              </p>
              <h2 className="mt-4 max-w-[14ch] font-serif text-[clamp(2.2rem,5vw,4rem)] leading-[0.95]">
                {copy.personas.title}
              </h2>
            </div>
            <div className="space-y-5">
              {copy.personas.items.map(([title, text]) => (
                <div key={title} className="border-t border-[var(--rule)] pt-5">
                  <p className="font-sans text-[11px] uppercase tracking-[0.16em] text-[var(--gold)]">{title}</p>
                  <p className="mt-2 font-sans text-[14px] leading-7 text-[var(--text-primary)]">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--rule)]">
        <div className="mx-auto max-w-[1240px] px-6 py-16 md:px-10">
          <div className="grid gap-12 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
            <div>
              <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">
                Pricing
              </p>
              <h2 className="mt-4 max-w-[14ch] font-serif text-[clamp(2.2rem,5vw,4rem)] leading-[0.95]">
                {copy.pricing.title}
              </h2>
              <p className="mt-6 max-w-[48ch] font-sans text-[15px] leading-8 text-[var(--text-secondary)]">
                {copy.pricing.subtitle}
              </p>
            </div>
            <div className="space-y-4">
              {copy.pricing.plans.map(([plan, label, text]) => (
                <div key={plan} className="grid gap-3 border-t border-[var(--rule)] pt-4 md:grid-cols-[140px_160px_minmax(0,1fr)]">
                  <p className="font-serif text-[28px] text-[var(--text-primary)]">{plan}</p>
                  <p className="font-sans text-[11px] uppercase tracking-[0.16em] text-[var(--gold)]">{label}</p>
                  <p className="font-sans text-[14px] leading-7 text-[var(--text-secondary)]">{text}</p>
                </div>
              ))}
              <Link
                to="/dantes/pricing"
                className="inline-block border border-[var(--rule-strong)] px-4 py-3 font-sans text-[11px] uppercase tracking-[0.16em] text-[var(--text-primary)] transition-colors hover:border-[var(--text-primary)]"
              >
                {copy.pricing.cta}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1240px] px-6 py-16 md:px-10">
          <div className="grid gap-12 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <div>
              <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">
                Security
              </p>
              <h2 className="mt-4 max-w-[14ch] font-serif text-[clamp(2.2rem,5vw,4rem)] leading-[0.95]">
                {copy.security.title}
              </h2>
            </div>
            <div>
              <div className="space-y-4">
                {copy.security.items.map((item) => (
                  <div key={item} className="border-t border-[var(--rule)] pt-4 font-sans text-[14px] leading-7 text-[var(--text-primary)]">
                    {item}
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/dantes/security"
                  className="border border-[var(--rule-strong)] px-4 py-3 font-sans text-[11px] uppercase tracking-[0.16em] text-[var(--text-primary)] transition-colors hover:border-[var(--text-primary)]"
                >
                  {copy.security.link}
                </Link>
                <a
                  href={process.env.REACT_APP_DANTES_APP_URL || "/login"}
                  className="border border-[rgba(158,138,90,0.5)] px-4 py-3 font-sans text-[11px] uppercase tracking-[0.16em] text-[var(--gold)] transition-colors hover:border-[var(--gold)]"
                >
                  {copy.footer.cta}
                </a>
              </div>
              <p className="mt-6 max-w-[52ch] font-sans text-[12px] leading-7 text-[var(--text-tertiary)]">
                {copy.footer.disclaimer}
              </p>
            </div>
          </div>
        </div>
      </section>
    </DantesShell>
  );
}
